import { supabase } from '../lib/supabase';

export const dashboardContentService = {
  // Posts of the Week
  async getPostOfWeek() {
    const { data, error } = await supabase
      ?.from('posts_of_week')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('display_order', { ascending: true })
      ?.limit(1)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    
    return data ? {
      id: data?.id,
      title: data?.title,
      caption: data?.caption,
      image: data?.image_path ? supabase?.storage?.from('marketing-assets')?.getPublicUrl(data?.image_path)?.data?.publicUrl : null,
      imageAlt: data?.image_alt,
      platform: data?.platform,
      hashtags: data?.hashtags || []
    } : null;
  },

  async createPostOfWeek(postData, file) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let imagePath = null;

    // Upload file if provided
    if (file) {
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `dashboard-content/posts/${timestamp}-${file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('marketing-assets')
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;
      imagePath = uploadData?.path;
    }

    const { data, error } = await supabase
      ?.from('posts_of_week')
      ?.insert({
        title: postData?.title,
        caption: postData?.caption,
        image_path: imagePath,
        image_alt: postData?.imageAlt,
        platform: postData?.platform,
        hashtags: postData?.hashtags || [],
        is_active: postData?.isActive ?? true,
        display_order: postData?.displayOrder ?? 0,
        created_by: user?.id
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async updatePostOfWeek(id, updates, file) {
    let imagePath = updates?.imagePath;

    // Upload new file if provided
    if (file) {
      // Get existing post to delete old file
      const { data: existingPost } = await supabase
        ?.from('posts_of_week')
        ?.select('image_path')
        ?.eq('id', id)
        ?.single();

      // Delete old file if exists
      if (existingPost?.image_path) {
        await supabase?.storage
          ?.from('marketing-assets')
          ?.remove([existingPost?.image_path]);
      }

      // Upload new file
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `dashboard-content/posts/${timestamp}-${file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('marketing-assets')
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;
      imagePath = uploadData?.path;
    }

    const updateData = {
      title: updates?.title,
      caption: updates?.caption,
      image_alt: updates?.imageAlt,
      platform: updates?.platform,
      hashtags: updates?.hashtags,
      is_active: updates?.isActive,
      display_order: updates?.displayOrder
    };

    // Only update image_path if we have a new file
    if (imagePath) {
      updateData.image_path = imagePath;
    }

    const { data, error } = await supabase
      ?.from('posts_of_week')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async deletePostOfWeek(id) {
    // Get post to delete file
    const { data: post } = await supabase
      ?.from('posts_of_week')
      ?.select('image_path')
      ?.eq('id', id)
      ?.single();

    // Delete file from storage
    if (post?.image_path) {
      await supabase?.storage
        ?.from('marketing-assets')
        ?.remove([post?.image_path]);
    }

    const { error } = await supabase
      ?.from('posts_of_week')
      ?.delete()
      ?.eq('id', id);

    if (error) throw error;
    return { success: true };
  },

  // Ready to Share Items
  async getReadyToShareItems() {
    const { data, error } = await supabase
      ?.from('ready_to_share_items')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('display_order', { ascending: true });

    if (error) throw error;
    
    return data?.map(item => ({
      id: item?.id,
      title: item?.title,
      description: item?.description,
      caption: item?.caption,
      image: item?.image_path ? supabase?.storage?.from('marketing-assets')?.getPublicUrl(item?.image_path)?.data?.publicUrl : null,
      imageAlt: item?.image_alt,
      contentType: item?.content_type,
      date: item?.date
    }));
  },

  async createReadyToShareItem(itemData, file) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let imagePath = null;

    // Upload file if provided
    if (file) {
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `dashboard-content/ready-to-share/${timestamp}-${file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('marketing-assets')
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;
      imagePath = uploadData?.path;
    }

    const { data, error } = await supabase
      ?.from('ready_to_share_items')
      ?.insert({
        title: itemData?.title,
        description: itemData?.description,
        caption: itemData?.caption,
        image_path: imagePath,
        image_alt: itemData?.imageAlt,
        content_type: itemData?.contentType,
        date: itemData?.date,
        is_active: itemData?.isActive ?? true,
        display_order: itemData?.displayOrder ?? 0,
        created_by: user?.id
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async updateReadyToShareItem(id, updates, file) {
    let imagePath = updates?.imagePath;

    // Upload new file if provided
    if (file) {
      // Get existing item to delete old file
      const { data: existingItem } = await supabase
        ?.from('ready_to_share_items')
        ?.select('image_path')
        ?.eq('id', id)
        ?.single();

      // Delete old file if exists
      if (existingItem?.image_path) {
        await supabase?.storage
          ?.from('marketing-assets')
          ?.remove([existingItem?.image_path]);
      }

      // Upload new file
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `dashboard-content/ready-to-share/${timestamp}-${file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('marketing-assets')
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;
      imagePath = uploadData?.path;
    }

    const updateData = {
      title: updates?.title,
      description: updates?.description,
      caption: updates?.caption,
      image_alt: updates?.imageAlt,
      content_type: updates?.contentType,
      date: updates?.date,
      is_active: updates?.isActive,
      display_order: updates?.displayOrder
    };

    // Only update image_path if we have a new file
    if (imagePath) {
      updateData.image_path = imagePath;
    }

    const { data, error } = await supabase
      ?.from('ready_to_share_items')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async deleteReadyToShareItem(id) {
    // Get item to delete file
    const { data: item } = await supabase
      ?.from('ready_to_share_items')
      ?.select('image_path')
      ?.eq('id', id)
      ?.single();

    // Delete file from storage
    if (item?.image_path) {
      await supabase?.storage
        ?.from('marketing-assets')
        ?.remove([item?.image_path]);
    }

    const { error } = await supabase
      ?.from('ready_to_share_items')
      ?.delete()
      ?.eq('id', id);

    if (error) throw error;
    return { success: true };
  }
};

export default dashboardContentService;